/**
 * Get-Your-Answer landing/library — API client.
 *
 * Two responsibilities:
 *   1. CTA tracking — every click that routes to app.kgob.com fires a
 *      fire-and-forget POST to kgob.com → bridge DB. This is OUR analytics
 *      only; app.kgob.com does NOT depend on it (per their integration spec).
 *   2. Build the outbound URL with `?topic=`, `?session_type=`, `?urgency=`,
 *      and `?utm_*` params per their contract.
 *
 * Outbound URL contract (per app.kgob.com's Session 197 R1 spec):
 *   Booking funnel:  app.kgob.com/ask-a-question/book
 *   Customer login:  app.kgob.com/ask-a-question/portal/login
 *
 * Accepted query params on /ask-a-question/book (ALL optional):
 *   • topic         — free-form topic_id (we pass irs_notices, back_taxes, etc.)
 *   • session_type  — quick_question | deep_dive | strategy (pre-selects step 1)
 *   • urgency       — low | medium | high (pre-selects urgency badge)
 *   • utm_source    — attribution (typically the placement, e.g. "library_card")
 *   • utm_medium    — attribution (typically "organic" / "internal")
 *   • utm_campaign  — attribution (typically the canonical topic, e.g. "cp2000")
 *   • utm_content   — attribution (typically the placement variant)
 *   • utm_term      — attribution (typically the question text or slug)
 *
 * IMPORTANT — app.kgob.com no longer accepts `?question=...`. Users type
 * their question on Step 1 of the funnel. We carry the question intent via
 * `utm_term` for attribution only.
 *
 * See: SEO-012 + SEO-013 in /app/docs/SEO_UI_CONSULTATION_LOG.md
 *      /app/docs/ASK_A_CPA_INTEGRATION_HANDOFF_FOR_APP_KGOB.md (companion)
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve the app.kgob.com destination URL with TWO independent safety nets:
 *   1. Production hostname (`kgob.com` / `www.kgob.com`) ALWAYS routes to
 *      `https://app.kgob.com` regardless of what env var is set — this means
 *      a stale preview env var carried over by a misconfigured deploy CANNOT
 *      send real users to the preview funnel.
 *   2. On non-production hostnames (preview / localhost), honor
 *      `REACT_APP_APP_KGOB_BASE_URL` if set, otherwise fail-safe to production.
 *
 * Called at click time (not module load) so the hostname is always current.
 */
export function resolveAppKgobBase() {
  if (typeof window !== "undefined") {
    const host = (window.location && window.location.hostname) || "";
    if (host === "kgob.com" || host === "www.kgob.com") {
      return "https://app.kgob.com";
    }
  }
  return process.env.REACT_APP_APP_KGOB_BASE_URL || "https://app.kgob.com";
}

// Maps kgob.com's internal session_type values to app.kgob.com's accepted values.
// Their spec only accepts: quick_question | deep_dive | strategy.
const SESSION_TYPE_MAP = {
  quick: "quick_question",
  quick_question: "quick_question",
  deep_dive: "deep_dive",
  strategy: "strategy",
};

/**
 * Build the booking URL on app.kgob.com with optional pre-fill + attribution.
 *
 * @param {string} source              — placement identifier; becomes utm_source
 * @param {Object} ctx                 — optional intent context
 * @param {string} ctx.topic           — topic id, e.g. "irs_notices"
 * @param {string} ctx.session_type    — quick | deep_dive | strategy
 * @param {string} ctx.urgency         — low | medium | high
 * @param {string} ctx.questionSlug    — canonical slug for attribution (becomes utm_term)
 * @param {string} ctx.questionText    — raw user-typed question for attribution
 * @param {string} ctx.campaign        — campaign tag (defaults to topic if present)
 */
export function buildOutboundUrl(source, ctx = {}) {
  const p = new URLSearchParams();
  if (ctx.topic) p.set("topic", ctx.topic);
  const st = SESSION_TYPE_MAP[ctx.session_type];
  if (st) p.set("session_type", st);
  if (ctx.urgency) p.set("urgency", ctx.urgency);
  // UTM attribution — what kgob.com knows that app.kgob.com wants reported
  if (source) p.set("utm_source", `kgob_${source}`);
  p.set("utm_medium", "internal");
  if (ctx.campaign || ctx.topic) {
    p.set("utm_campaign", ctx.campaign || ctx.topic);
  }
  if (ctx.questionSlug) p.set("utm_content", String(ctx.questionSlug).slice(0, 100));
  if (ctx.questionText) {
    // Truncate BEFORE encoding so the decoded value at app.kgob.com is ≤200 chars
    // (their funnel hard-422s at 201). Counting raw chars, not bytes.
    p.set("utm_term", String(ctx.questionText).slice(0, 200));
  }
  const qs = p.toString();
  const base = `${resolveAppKgobBase()}/ask-a-question/book`;
  return qs ? `${base}?${qs}` : base;
}

/**
 * Customer-login URL on app.kgob.com. No params accepted; returning customers
 * authenticate via email-code on their side.
 */
export function buildCustomerLoginUrl() {
  return `${resolveAppKgobBase()}/ask-a-question/portal/login`;
}

/**
 * Fire-and-forget tracking POST to OUR backend (kgob.com side). Resolves
 * immediately so the click navigation isn't blocked. Server-side failure is
 * silent (the URL still works).
 *
 * app.kgob.com does NOT depend on this — it's our own funnel analytics.
 */
export function trackCTA(source, ctx = {}) {
  try {
    const body = JSON.stringify({
      source,
      question: ctx.questionSlug || ctx.question || null,
      topic: ctx.topic || null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      path: typeof window !== "undefined" ? window.location.pathname : null,
    });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(
        `${BACKEND}/api/v1/get-your-answer/track-cta`,
        new Blob([body], { type: "application/json" })
      );
    } else {
      fetch(`${BACKEND}/api/v1/get-your-answer/track-cta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    // Never block on a tracking error.
  }
}

/**
 * Convenience: open the outbound booking URL with tracking.
 */
export function openWithTracking(source, ctx = {}) {
  trackCTA(source, ctx);
  if (typeof window !== "undefined") {
    window.location.href = buildOutboundUrl(source, ctx);
  }
}
